/**
 * Analytics data slice (P7 analytics, tenant `rnr_us` data only — S0.7). Shapes mirror the REAL routes in
 * api/src/modules/analytics/{routes,service}.js: every range is tenant/location-LOCAL days `from`..`to` (YYYY-MM-DD).
 *   GET /v1/app/analytics/summary?from&to&location     → jobs, rates, money (CM1/CM2/CM3)
 *   GET /v1/app/analytics/locations?from&to            → per-location rollup + daily series
 *   GET /v1/app/analytics/techs?from&to&location       → tech KPIs
 *   GET /v1/app/analytics/funnel?from&to&location      → call-to-cash steps (calls null until P4 voice ships)
 *   GET /v1/app/analytics/jobs?from&to&location        → per-job margin rows (CSV export)
 *   GET /v1/app/analytics/receptionist?from&to         → AI receptionist stats (ASSUMED; no route yet — P4/P7)
 */
import { APP, type Http } from '@app/core/http';

export interface Range { from: string; to: string; location?: string }

export interface Summary {
  from: string; to: string; location: string | null; fees_pending: boolean;
  jobs: { booked: number; completed: number; cancelled: number; duplicates: number; paid: number; fleet: number };
  rates: { completion_pct: number; cancellation_pct: number; duplicate_pct: number; decided_pct: number; on_time_pct: number };
  money: {
    revenue_cents: number; parts_cogs_cents: number; tech_pay_cents: number; card_fee_cents: number; cm1_cents: number; cm2_cents: number;
    cm2_pct: number; marketing_cents: number; cm3_cents: number; cm3_pct: number; aov_cents: number;
  };
}
export interface LocationRow { location_id: string; name: string; timezone: string; jobs: number; revenue_cents: number; cm1_cents: number; cm2_cents: number; cm2_pct: number; aov_cents: number }
export interface SeriesPoint { day: string; jobs: number; revenue_cents: number; cm2_cents: number }
export interface Locations { locations: LocationRow[]; series: SeriesPoint[] }
export interface TechKpi {
  tech_id: string; name: string; booked: number; completed: number; cancelled: number; paid: number; revenue_cents: number; cm2_cents: number;
  cm2_pct: number; avg_ticket_cents: number; completion_pct: number; cancellation_pct: number; jobs_per_day: number; on_time_pct: number;
  rating: number | null; ratings: number;
}
export interface Techs { techs: TechKpi[]; active_techs: number; avg_completions: number; avg_revenue_cents: number }
export interface FunnelStep { key: string; label: string; value: number | null }
export interface Receptionist {
  calls: number; ai_answered: number; staff_answered: number; missed: number; after_hours: number; booked: number; avg_duration_sec: number;
  /** Tenant-local hour of day 0–23. */
  by_hour: { hour: number; ai: number; staff: number }[];
}
export interface JobMarginRow {
  job_id: string; job_no: string; day_local: string; location_id: string | null; tech_id: string | null; revenue_cents: number; parts_cogs_cents: number;
  tech_pay_cents: number; card_fee_cents: number; cm1_cents: number; cm2_cents: number; is_fleet: boolean;
}

export interface Api {
  summary(r: Range): Promise<Summary>;
  locations(r: Range): Promise<Locations>;
  techs(r: Range): Promise<Techs>;
  funnel(r: Range): Promise<FunnelStep[]>;
  receptionist(r: Range): Promise<Receptionist>;
  jobs(r: Range): Promise<JobMarginRow[]>;
}

const q = (r: Range) => ({ query: { from: r.from, to: r.to, location: r.location || undefined } });
const n = (v: unknown) => (Number.isFinite(Number(v)) ? Number(v) : 0);

export const live = (http: Http): Api => ({
  summary: (r) => http.get<Summary>(`${APP}/analytics/summary`, q(r)),
  locations: (r) => http.get<any>(`${APP}/analytics/locations`, { query: { from: r.from, to: r.to } })
    .then((x) => ({ locations: x?.locations ?? [], series: (x?.series ?? []).map((s: any) => ({ ...s, day: String(s.day).slice(0, 10) })) })),
  techs: (r) => http.get<Techs>(`${APP}/analytics/techs`, q(r)),
  funnel: (r) => http.get<any>(`${APP}/analytics/funnel`, q(r)).then((x) => x?.steps ?? []),
  receptionist: (r) => http.get<any>(`${APP}/analytics/receptionist`, q(r)).then((x) => {
    const v = x?.receptionist ?? x ?? {};
    return {
      calls: n(v.calls), ai_answered: n(v.ai_answered), staff_answered: n(v.staff_answered), missed: n(v.missed), after_hours: n(v.after_hours),
      booked: n(v.booked), avg_duration_sec: n(v.avg_duration_sec),
      by_hour: Array.isArray(v.by_hour) ? v.by_hour.map((h: any) => ({ hour: n(h.hour), ai: n(h.ai), staff: n(h.staff) })) : [],
    };
  }),
  jobs: (r) => http.get<any>(`${APP}/analytics/jobs`, q(r)).then((x) => (x?.jobs ?? []).map((j: any) => ({ ...j, day_local: String(j.day_local).slice(0, 10) }))),
});
